
import { PostgrestError } from "@supabase/supabase-js";

/**
 * Type guard to check if an object is a PostgrestError
 */
export const isPostgrestError = (obj: any): obj is PostgrestError => {
  return obj && typeof obj === 'object' && 'message' in obj && 'code' in obj;
};

/**
 * Type guard to check if data is valid and not an error
 */
export const isValidData = <T>(data: T | PostgrestError | null | undefined): data is T => {
  return data !== null && data !== undefined && !isPostgrestError(data);
};

/**
 * Safe accessor for object properties with type safety
 */
export const safeGet = <T, K extends keyof T>(obj: T | null | undefined, key: K, defaultValue: T[K]): T[K] => {
  if (!obj) return defaultValue;
  return (obj as T)[key] ?? defaultValue;
};

/**
 * Type assertion helper for Supabase query results
 * Use this to safely cast data while handling possible errors
 */
export function asType<T>(data: any): T {
  return data as T;
}

/**
 * Safely convert any potential string or number to a string
 */
export function asString(value: any, defaultValue: string = ''): string {
  if (value === null || value === undefined) return defaultValue;
  return String(value);
}

/**
 * Safely convert any potential string or number to a number
 */
export function asNumber(value: any, defaultValue: number = 0): number {
  if (value === null || value === undefined) return defaultValue;
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  return isNaN(num) ? defaultValue : num;
}

/**
 * Safely convert any potential value to a boolean
 */
export function asBoolean(value: any, defaultValue: boolean = false): boolean {
  if (value === null || value === undefined) return defaultValue;
  return Boolean(value);
}

/**
 * Safely process database record avoiding type errors
 */
export function safeRecord<T>(record: any, defaults: Partial<T> = {}): T {
  if (!record || typeof record !== 'object') {
    return defaults as T;
  }
  
  return record as T;
}

/**
 * Safe accessor for deeply nested properties
 * Useful for handling nested Supabase responses
 */
export function safeDeepGet(obj: any, path: string, defaultValue: any = undefined): any {
  if (!obj) return defaultValue;
  
  const keys = path.split('.');
  let result = obj;
  
  for (const key of keys) {
    if (result === null || result === undefined || typeof result !== 'object') {
      return defaultValue;
    }
    
    result = result[key];
    
    if (result === undefined) {
      return defaultValue;
    }
  }
  
  return result ?? defaultValue;
}

/**
 * Safely extract property from Supabase query result by converting to string
 * Useful for handling type errors in StaffInvitesTab and similar components
 */
export function safeExtractString(obj: any, key: string, defaultValue: string = ''): string {
  try {
    // First handle case where obj is null/undefined
    if (!obj) return defaultValue;
    
    // Handle errors from Supabase
    if (isPostgrestError(obj)) return defaultValue;
    
    // Handle SelectQueryError objects
    if (obj.error === true) return defaultValue;
    
    // Extract the value and ensure it's a string
    const value = obj[key];
    return value !== null && value !== undefined ? String(value) : defaultValue;
  } catch (error) {
    console.error(`Error extracting ${key} from object:`, error);
    return defaultValue;
  }
}

/**
 * Safely extract property from Supabase query result by converting to boolean
 */
export function safeExtractBoolean(obj: any, key: string, defaultValue: boolean = false): boolean {
  try {
    // First handle case where obj is null/undefined
    if (!obj) return defaultValue;
    
    // Handle errors from Supabase
    if (isPostgrestError(obj)) return defaultValue;
    
    // Handle SelectQueryError objects
    if (obj.error === true) return defaultValue;
    
    // Extract the value and ensure it's a boolean
    const value = obj[key];
    return value !== null && value !== undefined ? Boolean(value) : defaultValue;
  } catch (error) {
    console.error(`Error extracting ${key} from object:`, error);
    return defaultValue;
  }
}

/**
 * Helper function to safely extract data from Supabase response
 * and convert it to an array of type T
 */
export function safeCastArray<T>(data: any, defaultValue: T[] = []): T[] {
  if (!data) return defaultValue;
  if (!Array.isArray(data)) return defaultValue;
  return data as T[];
}
